import { filter, uniqBy } from 'lodash';
import momentTimezone from 'moment-timezone';

import * as types from './constants';

const defaultTimezone = 'America/Los_Angeles';
const getMomentInTimezone = timezone => {
  if (timezone === 'UTC') return momentTimezone.utc();
  if (timezone) return momentTimezone().tz(timezone);
  return momentTimezone();
};

export const STEPS = {
  USERS: 'users',
  CONFIG: 'config',
  DETAILS: 'details'
};

const initialState = {
  step: STEPS.USERS,
  data: {
    date_start: getMomentInTimezone(defaultTimezone).format(),
    date_start_tz: defaultTimezone,
    date_end: getMomentInTimezone(defaultTimezone).add(1, 'month').endOf('month').set({ hour: 23, minute: 59, second: 0, millisecond: 0 }).format(),
    date_end_tz: defaultTimezone,
    max_bonus: '',
    users: []
  },
  errors: {
    active: false,
    details: {
      error: false,
      date_start: null,
      date_end: null
    },
    users: {
      error: false,
      value: null
    },
    config: {
      error: false,
      max_bonus: null
    },
    non_field_errors: null
  },
  unsavedChanges: false,
  connectedRowByUuid: {}
};

const actions = {
  [types.REIMBURSEMENT_MANAGE_FLOW_SET_STEP]: (state, { payload }) => {
    return { ...state, step: payload };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_SET_PROP]: (state, { payload }) => {
    return {
      ...state,
      data: {
        ...state.data,
        [payload.prop]: payload.value
      },
      unsavedChanges: !payload.disableUnsavedChanges
    };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_SET_UNSAVED_CHANGES]: (state, { payload }) => {
    return { ...state, unsavedChanges: payload };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_CLEAR]: () => {
    return { ...initialState };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_SET_ERRORS]: (state, { payload }) => {
    const newErrors = {
      ...initialState.errors,
      details: {
        ...initialState.errors.details,
        ...((payload.date_start || payload.starts_at) && { error: true, date_start: payload.date_start || payload.starts_at }),
        ...((payload.date_end || payload.ends_at) && { error: true, date_end: payload.date_end || payload.ends_at })
      },
      users: {
        ...(payload.users && { error: true, value: (typeof payload.users === 'string') ? payload.users : payload.users[0] })
      },
      config: {
        ...initialState.errors.config,
        ...((payload.max_bonus || payload.max_bonus_amount) && { error: true, max_bonus: payload.max_bonus || payload.max_bonus_amount })
      },
      non_field_errors: {
        ...(payload.non_field_errors && { error: true, value: payload.non_field_errors })
      }
    };

    return {
      ...state,
      errors: {
        ...newErrors,
        active: Object.values(newErrors).some(item => item && item.error === true)
      }
    };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_CLEAR_ERROR]: (state, { payload }) => {
    const newErrors = {
      ...state.errors,
      [payload]: initialState.errors[payload]
    };

    return {
      ...state,
      errors: {
        ...newErrors,
        active: false
      }
    };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_CLEAR_ERRORS]: state => {
    return {
      ...state,
      errors: {
        ...state.errors,
        active: false
      }
    };
  },

  // Users Step
  [types.REIMBURSEMENT_MANAGE_FLOW_ADD_USER]: (state, { payload }) => {
    const uuid = payload?.creator?.uuid;

    return {
      ...state,
      connectedRowByUuid: uuid
        ? { ...state.connectedRowByUuid, [uuid]: payload }
        : state.connectedRowByUuid,
      data: {
        ...state.data,
        users: uniqBy([payload.creator, ...state.data.users], 'uuid')
      },
      unsavedChanges: true
    };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_ADD_USERS]: (state, { payload }) => {
    return {
      ...state,
      data: {
        ...state.data,
        users: uniqBy([...payload, ...state.data.users], 'uuid')
      },
      unsavedChanges: true
    };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_REMOVE_USER]: (state, { payload }) => {
    const uuid = payload?.uuid;

    const nextMap = { ...state.connectedRowByUuid };
    if (uuid) delete nextMap[uuid];

    return {
      ...state,
      connectedRowByUuid: nextMap,
      data: {
        ...state.data,
        users: filter(state.data.users, x => x.uuid !== payload.uuid)
      },
      unsavedChanges: true
    };
  },

  [types.REIMBURSEMENT_MANAGE_FLOW_REMOVE_ALL_USERS]: state => {
    return {
      ...state,
      connectedRowByUuid: {},
      data: {
        ...state.data,
        users: []
      },
      unsavedChanges: true
    };
  },

  // Config Step
  [types.REIMBURSEMENT_MANAGE_FLOW_SET_CONFIG]: (state, { payload }) => {
    return {
      ...state,
      data: {
        ...state.data,
        [payload.prop]: payload.value
      },
      unsavedChanges: true
    };
  }
};

const reimbursementManageFlow = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default reimbursementManageFlow;
